/**
 * A piece of printing (receipt, label) drawn as a picture, in the browser.
 *
 * The page is laid out exactly as it would be printed, then drawn into a canvas
 * through an SVG image. An image cannot load anything from outside itself, so
 * the shop's fonts go in as data - otherwise the Arabic on the paper would be
 * in some fallback face and not the one on screen.
 */

let fontsCss: Promise<string> | null = null;

/** fonts.css with every font file built in, made once per visit. */
function embeddedFonts(): Promise<string> {
  fontsCss ??= (async () => {
    try {
      let css = await (await fetch('/fonts/fonts.css')).text();
      const urls = [...new Set([...css.matchAll(/url\(['"]?([^'")]+)['"]?\)/g)].map((m) => m[1]))];
      for (const u of urls) {
        const blob = await (await fetch(u)).blob();
        const data = await new Promise<string>((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(String(r.result));
          r.onerror = () => reject(r.error);
          r.readAsDataURL(blob);
        });
        css = css.split(u).join(data);
      }
      return css;
    } catch {
      fontsCss = null; // try again next time; this time the system fonts will do
      return '';
    }
  })();
  return fontsCss;
}

const PX_PER_MM = 96 / 25.4;

/** How tall the content is when laid out at this width, in CSS pixels. */
async function measure(html: string, widthPx: number): Promise<number> {
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText = `position:fixed;left:-20000px;top:0;width:${widthPx}px;height:10px;border:0;visibility:hidden`;
  document.body.appendChild(frame);
  try {
    const doc = frame.contentDocument!;
    doc.open();
    doc.write(`<!doctype html><html><head><meta charset="utf-8"><link rel="stylesheet" href="/fonts/fonts.css"></head><body style="margin:0">${html}</body></html>`);
    doc.close();
    const fonts = (doc as Document & { fonts?: FontFaceSet }).fonts;
    await Promise.race([fonts?.ready ?? Promise.resolve(), new Promise((r) => setTimeout(r, 1500))]);
    await new Promise((r) => setTimeout(r, 50));
    return Math.ceil(Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight));
  } finally {
    frame.remove();
  }
}

/**
 * Draw HTML at a width in millimetres onto a canvas exactly `dots` wide.
 * Height: fixed for a label, or as tall as the content for a receipt.
 */
export async function renderToCanvas(
  bodyHtml: string,
  opts: { widthMm: number; dots: number; heightMm?: number; dir?: 'rtl' | 'ltr'; lang?: string },
): Promise<HTMLCanvasElement> {
  const widthPx = opts.widthMm * PX_PER_MM;
  const base = `<style>
    * { box-sizing: border-box; }
    body { margin: 0; font-family: 'Cairo', 'Inter', system-ui, sans-serif; color: #000; background: #fff; width: ${opts.widthMm}mm; }
    .tnum { font-variant-numeric: tabular-nums; }
  </style>`;
  const content = base + bodyHtml;
  const heightPx = opts.heightMm ? opts.heightMm * PX_PER_MM : (await measure(content, widthPx)) + 24;

  // XHTML, because that is the only HTML an SVG image will read.
  const doc = document.implementation.createHTMLDocument('');
  doc.body.setAttribute('dir', opts.dir ?? document.documentElement.dir ?? 'ltr');
  doc.body.setAttribute('lang', opts.lang ?? document.documentElement.lang ?? 'en');
  doc.body.innerHTML = `<style>${await embeddedFonts()}</style>${content}`;
  const xhtml = new XMLSerializer().serializeToString(doc.body);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${widthPx}" height="${heightPx}"><foreignObject x="0" y="0" width="100%" height="100%">${xhtml}</foreignObject></svg>`;

  // A data address, not a blob: Chrome marks a canvas that has drawn an SVG
  // with HTML inside it from a blob as "tainted" and then refuses to hand back
  // its pixels. From a data address it does not.
  const img = new Image();
  img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  await img.decode();
  const scale = opts.dots / widthPx;
  const canvas = document.createElement('canvas');
  canvas.width = opts.dots;
  canvas.height = Math.max(1, Math.round(heightPx * scale));
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas;
}
